import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FaqBlock } from '../../../models/FaqBlock';
import { OpenService } from '../../../services/open-service';
import { form, FormField } from '@angular/forms/signals';
import { v4 as uuid } from 'uuid';
import { UpdateService } from '../../../services/update-service';
import { ToastrService } from 'ngx-toastr';
import { FaqCategory } from '../../../models/FaqCategory';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';


@Component({
  selector: 'app-edit-faq',
  imports: [FormField, CdkDropList, CdkDrag],
  templateUrl: './edit-faq.html',
  styleUrl: 'edit-faq.scss',
})
export class EditFaq implements OnInit {

  private openService = inject(OpenService);
  private updateService = inject(UpdateService);
  private toastrService = inject(ToastrService);

  editingCategoryIndex = signal<number | null>(null);
  editingCategoryOriginalLabel = signal<string>('');


  faqDataModel = signal<{ FAQ: FaqCategory[] }>({
    FAQ: []
  });

  faqDataForm = form(this.faqDataModel);

  isFormDirty = computed(() => {
    return this.faqDataForm().dirty();
  });

  dropListIds = computed(() =>
    this.faqDataModel().FAQ.map((_, i) => 'cat' + i)
  );

  ngOnInit(): void {
    this.fetchFaqData();
  }

  startEditCategory(catIndex: number, currentLabel: string) {
    if (this.editingCategoryIndex() !== null) {
      this.cancelEditCategory(this.editingCategoryIndex()!);
    }
    this.editingCategoryIndex.set(catIndex);
    this.editingCategoryOriginalLabel.set(currentLabel);
  }

  cancelEditCategory(catIndex: number) {
    this.faqDataModel.update(data => ({
      ...data,
      FAQ: data.FAQ!.map(category =>
        category.categoryIndex === catIndex
          ? {
            ...category,
            label: this.editingCategoryOriginalLabel()
          }
          : category
      ),
    }));
    this.editingCategoryIndex.set(null);
  }

  exitAndValidateEditCategory() {
    this.editingCategoryOriginalLabel.set('');
    this.editingCategoryIndex.set(null);
  }

  fetchFaqData(): void {
    this.openService.getFaqData().then(
      dto => this.faqDataModel.set({ FAQ: dto.FAQ })
    ).catch(
      err => this.toastrService.error('Erreur de recuperation', err));
  }

  addCategory() {
    this.faqDataModel.update(data => ({
      ...data,
      FAQ: [...data.FAQ!, { categoryUuid: uuid().toString(), label: '', blocks: [], categoryIndex: data.FAQ.length }],
    }));
    this.addBlock(this.faqDataModel().FAQ.length - 1);
  }

  removeCategory(catIndex: number) {
    this.faqDataModel.update(data => ({
      ...data,
      FAQ: data.FAQ!.filter((cat) => cat.categoryIndex !== catIndex),
    }));
    this.faqDataForm().markAsDirty();
  }

  addBlock(catIndex: number) {
    this.faqDataModel.update(data => ({
      ...data,
      FAQ: data.FAQ!.map(category =>
        category.categoryIndex === catIndex
          ? {
            ...category,
            blocks: [
              ...category.blocks,
              {
                question: "",
                answer: "",
                blockUuid: uuid().toString(),
                blockIndex: category.blocks.length,
              },
            ],
          }
          : category
      ),
    }));
    this.faqDataForm().markAsDirty();
  }

  removeBlock(blockIndex: number, catIndex: number) {
    this.faqDataModel.update(data => ({
      ...data,
      FAQ: data.FAQ!.map(category =>
        category.categoryIndex === catIndex
          ? {
            ...category,
            blocks: category.blocks
              .filter(block => block.blockIndex !== blockIndex)
              .map((block, i) => ({ ...block, blockIndex: i })),
          }
          : category
      ),
    }));
    this.faqDataForm().markAsDirty();
  }

  validate() {
    if (this.faqDataForm().dirty()) {
      this.updateService.saveFaqData(this.faqDataModel().FAQ).then(
        dto => {
          this.faqDataModel.set({ FAQ: dto.FAQ });
          this.faqDataForm().reset();
          this.toastrService.success('Mise à jour validée !');
        }
      ).catch(() => this.toastrService.error('Erreur de validation'))
    }
  }

  reset() {
    if (this.faqDataForm().dirty()) {
      this.fetchFaqData();
      this.faqDataForm().reset();
    }
  }



  drop(event: CdkDragDrop<any>, toCatIndex: number) {
    if (event.previousContainer === event.container) {
      // Réordonne dans la même catégorie
      this.faqDataModel.update(data => ({
        ...data,
        FAQ: data.FAQ.map(cat => {
          if (cat.categoryIndex !== toCatIndex) return cat;
          const blocks = [...cat.blocks];
          moveItemInArray(blocks, event.previousIndex, event.currentIndex);
          return {
            ...cat,
            blocks: blocks.map((b, i) => ({ ...b, blockIndex: i }))
          };
        })
      }));
    } else {
      const fromCatId = +event.previousContainer.id.replace('cat', '');

      this.faqDataModel.update(data => {
        const fromBlocks = [...data.FAQ.find(c => c.categoryIndex === fromCatId)!.blocks];
        const toBlocks = [...data.FAQ.find(c => c.categoryIndex === toCatIndex)!.blocks];

        const [moved] = fromBlocks.splice(event.previousIndex, 1);
        toBlocks.splice(event.currentIndex, 0, moved);

        const reindex = (arr: FaqBlock[]) => arr.map((b, i) => ({ ...b, blockIndex: i }));

        return {
          ...data,
          FAQ: data.FAQ.map(cat => {
            if (cat.categoryIndex === fromCatId) return { ...cat, blocks: reindex(fromBlocks) };
            if (cat.categoryIndex === toCatIndex) return { ...cat, blocks: reindex(toBlocks) };
            return cat;
          })
        };
      });
    }
    this.faqDataForm().markAsDirty();
  }

  moveCategory(catIdx: number, targetIdx: number): void {
  
  this.faqDataModel.update(data => {
    const FAQ = [...data.FAQ];
    [FAQ[catIdx], FAQ[targetIdx]] = [FAQ[targetIdx], FAQ[catIdx]];
    return {
      ...data,
      FAQ: FAQ.map((cat, i) => ({ ...cat, categoryIndex: i }))
    };
  });
  
  this.faqDataForm().markAsDirty();
}
}
